import { useEffect } from 'react';

/**
 * R3 — Safari focus-loop guard. On Safari, focus events inside the React tree
 * bubble up to the AEM shell's document-level focus handler, which calls
 * `element.focus()` on the SAME target and re-triggers React's synthetic
 * focus handler → infinite recursion → RangeError (~5% of Safari sessions on
 * PROD per RCA-5). Server sets `data-r3-focus-guard="on"` on the SSR-emitted
 * #react-coverme-container div only when COVERME_R3_FOCUS_GUARD=on, so the
 * flag is per-env-flippable via chart values without a rebuild. When enabled,
 * we install capture-phase focusin/focusout listeners on document that
 * stopPropagation for any focus event whose target is inside the container
 * — the shell's bubble-phase handler never sees them. Shell-owned elements
 * (nav, footer, AEM chrome) are outside the container so their focus events
 * pass through untouched. See working/coverme/
 * 2026-07-26-coverme-lcp-remediation-plan.md Task 3.
 */
export const useSafariFocusGuard = (): void => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const container = document.getElementById('react-coverme-container');
    if (container?.dataset.r3FocusGuard !== 'on') {
      return undefined;
    }
    const stopIfInside = (event: FocusEvent): void => {
      if (event.target instanceof Node && container.contains(event.target)) {
        event.stopPropagation();
      }
    };
    document.addEventListener('focusin', stopIfInside, true);
    document.addEventListener('focusout', stopIfInside, true);
    return () => {
      document.removeEventListener('focusin', stopIfInside, true);
      document.removeEventListener('focusout', stopIfInside, true);
    };
  }, []);
};
